// WidgetPanel: the universal panel container.
//
// Renders one panel slot. Every panel has a tab header so the active tab is the
// widget title. The active widget may portal controls into the reserved slot.
//
// Key behavior (performance): only the ACTIVE tab's widget is rendered. The
// TradingView chart is the exception - it is kept mounted (hidden via
// display:none, see dashboard.css) because remounting it is expensive (it
// re-inits the library and reloads data); it re-fits on reactivation via its
// own `isActive` effect + autosize. Every other inactive widget is unmounted so
// it does no render, store-subscription, layout, or canvas work while hidden.
// Chain/GEX history is preserved regardless because the app root samples into
// the stores independent of the active tab.

import React, { memo, useEffect, useRef, useState } from 'react';
import usePlaybackStore from '../../Store/playbackStore';
import LiveBadge from '../Common/LiveBadge';
import { getWidget } from './widgetRegistry';
import { WidgetPanelSlotProvider } from './WidgetPanelSlot';

function WidgetPanel({
  panel,
  onTabChange,
  configPaused = false,
  memberCredentials = null,
  headerContent = null,
}) {
  const mountedOnceRef = useRef(new Set());
  const activeTabRef = useRef(null);
  const [slotTarget, setSlotTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const widgets = panel.widgets || [];

  // Resolve the active widget id, guarding against a stale activeTab.
  const activeId =
    panel.activeTab && widgets.includes(panel.activeTab)
      ? panel.activeTab
      : widgets[0];

  const activeEntry = getWidget(activeId);
  const bare = activeEntry?.bare;
  const replaying = usePlaybackStore((state) => state.mode === 'playing');

  useEffect(() => {
    if (activeId) mountedOnceRef.current.add(activeId);
    activeTabRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  const panelClass = [
    'widget-panel',
    bare ? 'widget-panel--bare' : '',
    activeId ? `widget-panel--${activeId}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (widgets.length === 0) {
    return (
      <section className="widget-panel widget-panel--empty">
        {headerContent && (
          <header className="widget-panel__head">
            {headerContent}
            <div className="widget-panel__tail">
              <div className="widget-panel__slot" ref={setSlotTarget} />
            </div>
          </header>
        )}
        <div className="widget-panel__placeholder">No widget assigned</div>
      </section>
    );
  }

  return (
    <section className={panelClass}>
      <header className="widget-panel__head">
        {headerContent || (
          <div className="widget-tabs">
            {widgets.map((id) => {
              const entry = getWidget(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`widget-tab ${id === activeId ? 'is-active' : ''}`}
                  ref={id === activeId ? activeTabRef : null}
                  onClick={() => onTabChange?.(panel.id, id)}
                  aria-pressed={id === activeId}
                  aria-label={entry?.label ?? id}
                  title={entry?.label ?? id}
                >
                  {entry?.tabLabel ?? entry?.label ?? id}
                </button>
              );
            })}
          </div>
        )}
        {/* The reserved tail keeps controls and statuses in otherwise blank
            header space. Each lane scrolls independently when width is tight. */}
        <div className="widget-panel__tail">
          <div className="widget-panel__statuses">
            {replaying && <LiveBadge />}
            <div className="widget-panel__status-slot" ref={setStatusTarget} />
          </div>
          <div className="widget-panel__slot" ref={setSlotTarget} />
        </div>
      </header>

      <div className="widget-panel__body">
        {widgets.map((id) => {
          const entry = getWidget(id);
          if (!entry) return null;
          const Cmp = entry.component;
          // Chart gets isActive=false while config modal is open so the
          // isActive effect fires resize + right-edge re-pin on modal close.
          const isActive = id === activeId && (id !== 'chart' || !configPaused);
          const keepMounted = id === 'chart' && mountedOnceRef.current.has(id);
          return (
            <div
              key={id}
              className={`widget-pane widget-pane--${id} ${isActive ? 'is-active' : 'is-hidden'}`}
              aria-hidden={!isActive}
            >
              {(isActive || keepMounted) && (
                <WidgetPanelSlotProvider
                  target={isActive ? slotTarget : null}
                  statusTarget={isActive ? statusTarget : null}
                >
                  <Cmp isActive={isActive} memberCredentials={memberCredentials} />
                </WidgetPanelSlotProvider>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default memo(WidgetPanel);
